import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const mobileMenuRef = useRef(null);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/main-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Vista general de proyectos y coordinación de agentes'
    },
    {
      label: 'Proyectos',
      path: '/project-detail-view',
      icon: 'FolderTree',
      tooltip: 'Análisis detallado de proyectos AFOLU'
    },
    {
      label: 'Documentos',
      path: '/document-management-center',
      icon: 'FolderOpen',
      tooltip: 'Gestión centralizada de documentos con control de versiones'
    },
    {
      label: 'Alertas',
      path: '/real-time-alert-center',
      icon: 'AlertTriangle',
      tooltip: 'Sistema de alertas en tiempo real con enrutamiento automático'
    },
    {
      label: 'Informes',
      path: '/report-rendering-view',
      icon: 'FileText',
      tooltip: 'Documentación técnica y reportes de validación'
    },
    {
      label: 'Reportes AOI',
      path: '/aoi-reports',
      icon: 'MapPin',
      tooltip: 'Generación de reportes para Áreas de Interés'
    },
    {
      label: 'Chat Agentes',
      path: '/agent-chat',
      icon: 'MessageSquare',
      tooltip: 'Chat en tiempo real con agentes especializados'
    },
    {
      label: 'Training Hub',
      path: '/agent-training-hub',
      icon: 'GraduationCap',
      tooltip: 'Recursos de capacitación y desarrollo de habilidades'
    }
  ];

  useEffect(() => {
    const darkModePreference = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(darkModePreference);
    if (darkModePreference) {
      document.documentElement?.classList?.add('dark');
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event?.target)) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode?.toString());
    
    if (newDarkMode) {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-1000 bg-card border-b border-border shadow-sm">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg transition-all duration-250 hover:bg-primary/20">
                <Icon name="Leaf" size={24} color="var(--color-primary)" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-foreground font-mono">CarbonAgentHub</h1>
                <p className="text-xs text-muted-foreground caption">Coordinación de Mercados de Carbono</p>
              </div>
            </div>

            <nav className="hidden lg:flex items-center gap-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  title={item?.tooltip}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm
                    transition-all duration-250 focus-ring
                    ${isActive(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              ))}
              <Link
                to="/agent-analytics-dashboard"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/10 transition-colors text-foreground"
              >
                <Icon name="BarChart3" size={18} />
                <span className="text-sm font-medium">Agent Analytics</span>
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              iconName={isDarkMode ? 'Sun' : 'Moon'}
              iconSize={20}
              className="hidden sm:flex"
            />

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md text-foreground hover:bg-muted transition-all duration-250 focus-ring"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-1100 lg:hidden">
          <div className="fixed inset-0 bg-secondary" onClick={() => setMobileMenuOpen(false)} />
          
          <div
            ref={mobileMenuRef}
            className="fixed top-16 left-0 right-0 bottom-0 bg-card overflow-y-auto"
            style={{ animation: 'slideDown 250ms cubic-bezier(0.4, 0, 0.2, 1)' }}
          >
            <nav className="flex flex-col p-4 gap-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md font-medium text-base
                    transition-all duration-250 focus-ring text-left
                    ${isActive(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  <div className="flex-1">
                    <div className="font-medium">{item?.label}</div>
                    <div className="text-xs opacity-80 caption mt-0.5">{item?.tooltip}</div>
                  </div>
                </button>
              ))}

              <div className="mt-6 pt-6 border-t border-border">
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center gap-3 px-4 py-3 w-full rounded-md text-foreground hover:bg-muted transition-all duration-250 focus-ring"
                >
                  <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={20} />
                  <span className="font-medium">{isDarkMode ? 'Modo Claro' : 'Modo Oscuro'}</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;